import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";
import { getSignInPathWithRedirect, getSignUpPathWithRedirect } from "routes/signInRedirect";

import styles from "./TreeAccessNotice.module.css";

export type TreeAccessNoticeProps = {
	/**
	 * "notFound" when the tree does not exist (or its owner is deactivated); "noAccess" when it exists
	 * but is private or draft and the visitor is not a member.
	 */
	reason: "notFound" | "noAccess";
	/** In-app path to return to after signing in — the tree URL the visitor came for. */
	returnTo: string;
	/** True when the visitor is signed in; they get no sign-in prompt, only a way out. */
	hasSession: boolean;
};

/**
 * Terminal state for a tree URL the visitor cannot see. Private trees are shared by link, so an
 * anonymous visitor may well be a member who simply is not signed in — say plainly that the tree is
 * private and offer sign-in, rather than bouncing them to a login form with no explanation.
 */
export const TreeAccessNotice = ({ reason, returnTo, hasSession }: TreeAccessNoticeProps) => {
	const { t } = useTranslation("homePage");

	return (
		<div className={styles.notice} role="status">
			<span aria-hidden="true" className={styles.icon}>
				{reason === "notFound" ? (
					<svg fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
						<circle cx="12" cy="12" r="9" />
						<path d="M9 9h.01M15 9h.01M9 15.5c1.6-1.2 4.4-1.2 6 0" strokeLinecap="round" />
					</svg>
				) : (
					<svg fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
						<rect height="10" rx="2" width="14" x="5" y="11" />
						<path d="M8 11V7.5a4 4 0 0 1 8 0V11" strokeLinecap="round" />
					</svg>
				)}
			</span>

			<h1 className={styles.title}>{t(`access.${reason}Title`)}</h1>
			<p className={styles.message}>
				{t(hasSession ? `access.${reason}Message` : `access.${reason}MessageAnonymous`)}
			</p>

			<div className={styles.actions}>
				{hasSession ? (
					<Link className={styles.primaryAction} to={Paths.root}>
						{t("access.backHome")}
					</Link>
				) : (
					<>
						<Link className={styles.primaryAction} to={getSignInPathWithRedirect(returnTo)}>
							{t("view.signIn")}
						</Link>
						<Link className={styles.secondaryAction} to={getSignUpPathWithRedirect(returnTo)}>
							{t("access.createAccount")}
						</Link>
					</>
				)}
			</div>
		</div>
	);
};

export default TreeAccessNotice;
